/**
 * Bridge: Cognito group → permission list for Return Manager.
 *
 * Context:
 *   The backoffice-layer Provider expects `featuresPermissions[0].actions` to be
 *   populated from `GET /meta/refresh-permissions` (AVP-backed flow, used by BAM).
 *   Return Manager still runs a legacy group-based authorization model, so that
 *   endpoint returns nothing here and the Provider loops on
 *   "permissions not yet loaded, waiting...".
 *
 *   This module seeds the auth store with the permission list derived from the
 *   user's Cognito group (`cognito:groups[0]`) via the local `authorizations`
 *   map. It only fills the gap when the API returned nothing — it never
 *   overrides a non-empty permission set.
 *
 * Security posture:
 *   - Cognito is the authentication gate: any user reaching this code has
 *     already been authenticated and provisioned. Falling back to the least-
 *     privileged group (`USER`) on an unknown/missing claim is a graceful
 *     degradation, not a privilege elevation — `USER` holds the lowest set
 *     of permissions in `authorizations`.
 *   - Unknown groups are still reported via `debug.warn` so misconfigurations
 *     surface in the browser console and Sentry breadcrumbs instead of going
 *     silent.
 *   - Prototype-safe: group lookup uses `hasOwnProperty`, never `in`, to
 *     block matches against inherited keys.
 *
 * Remove this module when Return Manager's API exposes `/meta/refresh-permissions`
 * returning the same shape as BAM — then the layer's standard flow will hydrate
 * permissions on its own.
 */
import {
  setCurrentPermissions,
  useAuth,
} from '@fastlessjs/backoffice-layer/dist/hooks/useAuth';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';

import { authorizations } from './authorizations';

debug.registerComponent('legacyGroupPermissions');

/**
 * Prototype-safe membership test against the `authorizations` map. Using
 * `Object.prototype.hasOwnProperty.call` instead of `in` prevents spurious
 * matches against inherited keys (`__proto__`, `constructor`, `toString`, …).
 */
const isKnownGroup = (value: string): boolean =>
  Object.prototype.hasOwnProperty.call(authorizations, value);

/** Default group used when the Cognito claim is missing or unknown. */
const DEFAULT_GROUP = 'USER';

/**
 * Resolve a known group key from a raw Cognito group claim.
 * Cognito groups may be namespaced (e.g. `tenant-x/ADMIN`); we probe the raw
 * value, then the last segment, then the first segment.
 * Returns `null` when no known group matches — callers fall back to
 * `DEFAULT_GROUP` (least-privileged) for graceful degradation.
 */
const resolveGroupKey = (group: string | undefined): string | null => {
  const raw = (group ?? '').trim();
  if (!raw) return null;
  if (isKnownGroup(raw)) return raw;

  const segments = raw.split('/').filter(Boolean);

  const last = segments.at(-1);
  if (last && isKnownGroup(last)) return last;

  const [first] = segments;
  if (first && isKnownGroup(first)) return first;

  return null;
};

/**
 * Seed the auth store with the legacy permission list when:
 *   (1) the user is authenticated, AND
 *   (2) `featuresPermissions[0].actions` is empty (no AVP response).
 *
 * No-op otherwise. Idempotent and safe to call from subscribe listeners.
 */
const applyLegacyGroupPermissionsIfNeeded = (): void => {
  const { user, getPermissions } = useAuth.getState();
  if (!user?.email) return;

  const actions = getPermissions() ?? [];
  if (actions.length > 0) return;

  const resolved = resolveGroupKey(user.group);
  const groupKey = resolved ?? DEFAULT_GROUP;

  if (!resolved) {
    debug.warn(
      'legacyGroupPermissions',
      `No authorizations entry for Cognito group "${
        user.group ?? ''
      }" — falling back to "${DEFAULT_GROUP}"`,
    );
  }

  const list = authorizations[groupKey] ?? [];
  setCurrentPermissions([{ enterpriseId: [], actions: [...list] }]);

  debug.log(
    'legacyGroupPermissions',
    `Seeded legacy group permissions for "${groupKey}" (${list.length} actions)`,
  );
};

/**
 * Wire the bridge to the auth store. Returns an `unsubscribe` handle (only
 * needed for tests — the SPA root never unmounts).
 *
 * Call once from `main.tsx` after `root.render(...)`. The subscription is
 * cheap: the inner function short-circuits unless permissions are actually
 * empty for an authenticated user.
 */
export const subscribeLegacyGroupPermissions = (): (() => void) => {
  applyLegacyGroupPermissionsIfNeeded();
  return useAuth.subscribe(applyLegacyGroupPermissionsIfNeeded);
};

debug.markInitialized('legacyGroupPermissions');
